// Global Instructions Rule Applied!
//
// The "Intercompany transfer" banner on the standard Capital Call form
// (owner rulings 2026-09-28): a transfer from a consolidated fund's primary
// to a satellite is built as the satellite call it books, in this form.
// Purely presentational - the form owns the lifecycle (Book transfer /
// Revert to Draft / Delete through the fgc__ transfer orchestrators).
//
// Shows the source (the pool of every issued call, or an equalization
// call), the pool's untransferred amount per bucket, and - for an SBIC
// satellite - the Regulatory Capital at the fee period start before and
// after this transfer: the fee lines are priced on the capital the transfer
// itself pays in (the fee solver iterates to that fixed point on Compute).

import React from 'react'
import { Alert, Space, Tag, Typography } from 'antd'
import dayjs from 'dayjs'
import NumberText from '../../../../core/components/NumberText.tsx'
import type { CapitalCallTransferContext } from '../../../../state_manager/data_models/capital_call_detail.ts'

const { Text } = Typography

const BUCKET_LABELS: Record<string, string> = {
  investment: 'investments',
  expenses: 'expenses',
  fees: 'mgmt fees'
}

interface TransferCallBannerProps {
  transfer: CapitalCallTransferContext
}

export default function TransferCallBanner({ transfer }: TransferCallBannerProps) {
  const booked = transfer.status_label === 'Booked'
  const buckets = transfer.pool?.buckets ?? []
  const rcBefore = transfer.regulatory_capital_before ?? null
  const rcAfter = transfer.regulatory_capital_after ?? null
  // once booked the transfer IS in the booked position (before = after)
  const showRegulatoryCapital = !booked && rcAfter != null && rcAfter > 0 && rcBefore != null
  const feeFrom = transfer.fee_period_from ? dayjs(transfer.fee_period_from).format('MM/DD/YYYY') : null

  return (
    <Alert
      type='info'
      showIcon
      message={
        <Space wrap size={6}>
          <span>
            Intercompany transfer — {transfer.primary_fund_name ?? 'primary fund'} →{' '}
            {transfer.satellite_fund_name ?? 'satellite'}
          </span>
          <Tag color={booked ? 'green' : 'blue'}>{transfer.status_label ?? 'Draft'}</Tag>
        </Space>
      }
      description={
        <div className='flex flex-col gap-1'>
          <Text type='secondary'>
            {transfer.is_equalization
              ? `Source: ${transfer.source_label} — the non-fee lines split by what each investor was called on it × its satellite share.`
              : 'Source: all issued calls (pooled) — every line splits by satellite share; fee lines are the satellite’s own fees for their period.'}{' '}
            A set gross amount is hit exactly: the balance line takes what the other lines leave.
            {booked
              ? ' Booked — Revert to Draft to change it.'
              : ' Compute prices the fees, Book transfer issues this call and books the intercompany pair.'}
          </Text>
          {buckets.length > 0 ? (
            <Space wrap size={12}>
              <Text strong>Pool not yet transferred:</Text>
              {buckets.map((b) => (
                <Text key={b.key} type='secondary'>
                  {BUCKET_LABELS[b.key] ?? b.label ?? b.key} <NumberText value={b.prog__retained} />
                </Text>
              ))}
            </Space>
          ) : null}
          {showRegulatoryCapital ? (
            <Text type='secondary'>
              Regulatory capital at {feeFrom ?? 'the fee period start'}: <NumberText value={rcBefore} /> booked →{' '}
              <NumberText value={rcAfter} /> with this transfer paid in — the fees are priced on the latter.
            </Text>
          ) : null}
          {transfer.notes ? <Text type='secondary'>Notes: {transfer.notes}</Text> : null}
        </div>
      }
    />
  )
}
